import React, { useEffect, useState } from 'react';
import styles from './gerador.module.scss';
import type { BannerConfig, CouponConfig, PageConfig } from './types.ts';
import { shadowCss } from './utils.ts';

type PreviewProps = {
    page: PageConfig;
    selectedId: string | null;
    onSelectBanner: (id: string) => void;
};

export const Preview: React.FC<PreviewProps> = ({ page, selectedId, onSelectBanner }) => {
    const [openCouponId, setOpenCouponId] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);

    const couponBanner = page.banners.find((banner) => banner.id === openCouponId);
    const coupon = couponBanner?.coupon.enabled ? couponBanner.coupon : null;

    useEffect(() => {
        if (openCouponId && !coupon) setOpenCouponId(null);
    }, [openCouponId, coupon]);

    useEffect(() => {
        if (!openCouponId) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpenCouponId(null);
        };
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = prev;
            window.removeEventListener('keydown', onKey);
        };
    }, [openCouponId]);

    useEffect(() => {
        setCopied(false);
    }, [openCouponId]);

    const handleCopy = async () => {
        if (!coupon?.code) return;
        try {
            await navigator.clipboard.writeText(coupon.code);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1800);
        } catch {
            setCopied(false);
        }
    };

    return (
        <div className={styles.phone} data-tour="preview">
            <div className={styles.phoneBar} />
            <div
                className={styles.phoneScreen}
                style={{
                    backgroundColor: page.backgroundColor,
                    backgroundImage: page.backgroundImage ? `url(${page.backgroundImage})` : undefined,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }}
            >
                <div className={styles.previewPage} style={{ maxWidth: page.maxWidth }}>
                {page.profileImage ? (
                    <img
                        src={page.profileImage}
                        alt="Foto de perfil"
                        className={styles.previewProfile}
                    />
                ) : (
                    <div className={styles.previewProfileMissing}>Foto de perfil</div>
                )}

                <div
                    className={styles.previewInner}
                    style={{
                        padding: `0 ${page.pagePaddingX}px ${page.pagePaddingY}px`,
                    }}
                >
                    <div className={styles.previewBanners} style={{ gap: page.bannerGap }}>
                        {page.banners.map((banner) => (
                            <BannerButton
                                key={banner.id}
                                banner={banner}
                                selected={selectedId === banner.id}
                                onClick={() => {
                                    onSelectBanner(banner.id);
                                    if (banner.coupon.enabled) {
                                        setOpenCouponId(banner.id);
                                    }
                                }}
                            />
                        ))}
                    </div>
                </div>
                </div>
            </div>

            {coupon ? (
                <CouponModal
                    coupon={coupon}
                    copied={copied}
                    onCopy={handleCopy}
                    onClose={() => setOpenCouponId(null)}
                />
            ) : null}
        </div>
    );
};

const BannerButton: React.FC<{
    banner: BannerConfig;
    selected: boolean;
    onClick: () => void;
}> = ({ banner, selected, onClick }) => {
    const inner = (
        <span
            className={styles.previewBannerTitle}
            style={{
                fontSize: banner.fontSize,
                letterSpacing: `${banner.letterSpacing}px`,
                textAlign: banner.titleAlign,
                textTransform: banner.uppercase ? 'uppercase' : 'none',
                color: banner.textColor,
            }}
        >
            {banner.title || 'Título'}
        </span>
    );

    const style: React.CSSProperties = {
        width: '100%',
        alignSelf: 'stretch',
        marginTop: banner.marginTop,
        marginBottom: banner.marginBottom,
        padding: `${banner.paddingY}px ${banner.paddingX}px`,
        minHeight: banner.minHeight,
        borderRadius: banner.borderRadius,
        background: banner.backgroundColor,
        color: banner.textColor,
        border: `${banner.borderWidth}px solid ${banner.borderColor}`,
        boxShadow: shadowCss(banner.shadow),
        justifyContent: banner.titleAlign === 'left' ? 'flex-start' : banner.titleAlign === 'right' ? 'flex-end' : 'center',
    };

    if (banner.coupon.enabled) {
        return (
            <button
                type="button"
                className={`${styles.previewBanner} ${selected ? styles.previewBannerSelected : ''}`}
                style={style}
                onClick={onClick}
            >
                {inner}
            </button>
        );
    }

    if (banner.href) {
        return (
            <a
                href={banner.href}
                target="_blank"
                rel="noreferrer"
                className={`${styles.previewBanner} ${selected ? styles.previewBannerSelected : ''}`}
                style={style}
                onClick={(e) => {
                    e.preventDefault();
                    onClick();
                }}
            >
                {inner}
            </a>
        );
    }

    return (
        <button
            type="button"
            className={`${styles.previewBanner} ${selected ? styles.previewBannerSelected : ''}`}
            style={style}
            onClick={onClick}
        >
            {inner}
        </button>
    );
};

export const CouponModal: React.FC<{
    coupon: CouponConfig;
    copied: boolean;
    onCopy: () => void;
    onClose: () => void;
}> = ({ coupon, copied, onCopy, onClose }) => (
    <div className={styles.modalOverlay} onClick={onClose} role="presentation">
        <div
            className={styles.modal}
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            style={{
                background: `linear-gradient(180deg, ${coupon.modalBg} 0%, ${coupon.modalBg2} 100%)`,
                color: coupon.modalText,
            }}
        >
            <button type="button" className={styles.modalClose} onClick={onClose} aria-label="Fechar">
                ×
            </button>
            <p className={styles.modalEyebrow} style={{ color: coupon.modalText }}>
                {coupon.eyebrow || 'Cupom exclusivo'}
            </p>
            <h2 className={styles.modalBrand} style={{ color: coupon.modalText }}>
                {coupon.brand || 'Marca'}
            </h2>

            {coupon.code ? (
                <div className={styles.cupomBox} style={{ background: coupon.boxBg }}>
                    <span className={styles.cupomLabel}>Seu cupom</span>
                    <span className={styles.cupomCode} style={{ color: coupon.codeColor }}>{coupon.code}</span>
                    {coupon.discount ? (
                        <span className={styles.cupomDesconto} style={{ color: coupon.codeColor }}>{coupon.discount}</span>
                    ) : null}
                    <button type="button" className={styles.copyBtn} onClick={onCopy} style={{ color: '#fff' }}>
                        {copied ? 'Copiado!' : 'Copiar cupom'}
                    </button>
                </div>
            ) : coupon.discount ? (
                <div className={styles.cupomBox} style={{ background: coupon.boxBg }}>
                    <span className={styles.cupomLabel}>Desconto</span>
                    <span className={styles.cupomCode} style={{ color: coupon.codeColor }}>{coupon.discount}</span>
                </div>
            ) : (
                <div className={styles.cupomBox} style={{ background: coupon.boxBg }}>
                    <span className={styles.cupomLabel}>Cupom</span>
                    <span className={styles.cupomEmpty}>{coupon.emptyText}</span>
                </div>
            )}

            {coupon.siteUrl ? (
                <a
                    href={coupon.siteUrl}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.siteBtn}
                    style={{ background: coupon.buttonBg, color: coupon.buttonText }}
                >
                    {coupon.ctaLabel || 'Ir para o site'}
                </a>
            ) : (
                <span
                    className={styles.siteBtn}
                    style={{ background: coupon.buttonBg, color: coupon.buttonText, opacity: 0.7 }}
                >
                    {coupon.ctaLabel || 'Ir para o site'}
                </span>
            )}
        </div>
    </div>
);
